import type { GoodVibesTimeFrame } from "@domains/good-vibes/shared/goodVibesTimeFrame.ts";
import type { GoodVibesVisibility } from "@domains/good-vibes/shared/goodVibesVisibility.ts";
import type { ReportingScope } from "@packages/core/organization/scope.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { ScopeSelector } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { useAnalyticsScopeClient } from "../api/query.ts";
import { AnalyticsFilterDimensions, useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import { useGoodVibesFilters } from "../hooks/useGoodVibesFilters.ts";
import { GoodVibesTimeFrameSelect } from "./GoodVibesTimeFrameSelect.tsx";
import { GoodVibesVisibilitySelect } from "./GoodVibesVisibilitySelect.tsx";

export function GoodVibesFiltersHeading() {
    const [{ scope, timeFrame, visibility }, setFilters] = useGoodVibesFilters();
    const scopeClient = useAnalyticsScopeClient();

    const { data: previews, isPending } = useQuery(createQueryOptions(scopeClient.getPreviews.queryOptions(), { throwOnError: false }));

    const { trackFilterApplied, trackAudienceScopeChanged } = useAnalyticsTracking();

    const handleScopeChange = useCallback(
        (next: ReportingScope) => {
            void setFilters({ scope: next });
            trackAudienceScopeChanged(next);
        },
        [setFilters, trackAudienceScopeChanged]
    );
    const handleTimeFrameChange = useCallback(
        (next: GoodVibesTimeFrame) => {
            void setFilters({ timeFrame: next });
            trackFilterApplied(AnalyticsFilterDimensions.TimeFrame);
        },
        [setFilters, trackFilterApplied]
    );
    const handleVisibilityChange = useCallback(
        (next: GoodVibesVisibility) => {
            void setFilters({ visibility: next });
            trackFilterApplied(AnalyticsFilterDimensions.Visibility);
        },
        [setFilters, trackFilterApplied]
    );

    return (
        <ScopeSelector
            scope={scope}
            onScopeChange={handleScopeChange}
            previews={previews}
            isPreviewsPending={isPending}
            trailingContent={
                <div className="flex flex-wrap items-center gap-2">
                    <GoodVibesTimeFrameSelect timeFrame={timeFrame} onTimeFrameChange={handleTimeFrameChange} />
                    <GoodVibesVisibilitySelect visibility={visibility} onVisibilityChange={handleVisibilityChange} />
                </div>
            }
        />
    );
}
